import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useEditor } from '@toasttab/sites-components'
import { t } from 'src/translations'

import {
  DiningOptionBehavior,
  useGetShippingOptionsQuery
} from 'src/apollo/onlineOrdering'
import FormattedPrice from 'src/shared/components/common/price/FormattedPrice'
import Link from 'shared/components/common/link'
import { usePopoverContext } from 'shared/components/common/popover/PopoverContext'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { useRestaurantRoutes } from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'

import { CART_POPOVER_CONTEXT_KEY } from 'public/components/default_template/online_ordering/cart/CartModal'

import { getAppliedFundedOffersTotal } from 'public/components/default_template/offers/appliedFundedOffers'
import {
  calculateDiscountsTotal,
  calculateSubtotalExcludingShippingFromCart,
  CartLineItems,
  getCheckoutEligibleItems,
  ItemSource,
  useCartLineItems
} from 'public/components/default_template/online_ordering/cart/cartUtils'
import { useValidateCartForShipping } from 'public/components/default_template/online_ordering/cart/useValidateCartForShipping'
import FreeShippingProgressBar from 'public/components/default_template/online_ordering/cart/FreeShippingProgressBar'
import FreeDeliveryProgress from 'public/components/default_template/online_ordering/cart/FreeDeliveryProgress'
import OrderItems, {
  OrderPrices
} from 'public/components/default_template/online_ordering/order/OrderItems'
import Upsell from 'public/components/default_template/online_ordering/upsell/Upsell'
import { useCart } from 'public/components/online_ordering/CartContext'
import { CartSelection } from 'public/components/online_ordering/types'
import { Channel } from 'public/js/siteUtilities'
import { useDelivery } from 'public/components/online_ordering/DeliveryContext'
import { useFulfillment } from 'public/components/online_ordering/FulfillmentContext'
import { useModal } from 'src/shared/components/common/modal/useModal'
import CartOffer from './offers/CartOffer'
import ItemModal from '../item_modal/ItemModal'
import { useUpsell } from 'src/public/components/online_ordering/UpsellContext'
import { UpsellItem } from 'public/components/default_template/online_ordering/upsell/UpsellItem'
import CartItemV2 from './CartItemV2'
import { ButtonLink } from 'src/shared/components/common/button'

import { INVALID_CONTENT_CODE, INVALID_FULFILLMENT_CODE } from './constants'
import { useFlags } from 'src/shared/components/common/feature_flags/useFlags'
import { useIsIntlRestaurant } from 'src/lib/js/hooks/useIsIntlRestaurant'
import {
  useA11y,
  A11yProvider
} from 'src/public/components/online_ordering/useA11y'
import { useButtonsConfig } from 'src/shared/components/common/button/useButtonsConfig'
import { usePageDomains } from 'src/public/js/PageDomainContext'
import { getToasttabCheckoutUrl } from 'src/shared/js/utils/DomainUtils'
import { resources } from 'config'
import ToastProduct from 'src/public/components/online_ordering/ToastProduct'

export type ShippingValidationProblem = {
  code?: string | null
  message?: string | null
  itemGuid?: string | null
  itemGroupGuid?: string | null
}

const hasInvalidContentProblemForSelection = (
  selection: CartSelection,
  problems: ShippingValidationProblem[]
): ShippingValidationProblem | undefined => {
  if (!selection) return undefined
  const { itemGuid, itemGroupGuid } = selection
  const matchingProblem = problems.find(
    (p) =>
      (p.code === INVALID_CONTENT_CODE ||
        p.code === INVALID_FULFILLMENT_CODE) &&
      p.itemGuid &&
      itemGuid &&
      p.itemGuid === itemGuid &&
      p.itemGroupGuid &&
      itemGroupGuid &&
      p.itemGroupGuid === itemGroupGuid
  )
  return matchingProblem
}

export const CartItems = ({
  editable,
  validationProblems = [],
  itemSource
}: {
  editable?: boolean
  validationProblems?: ShippingValidationProblem[]
  itemSource?: ItemSource
}) => {
  const { cart, cartGuid, error, channel } = useCart()
  const hasItems = !!getCheckoutEligibleItems(cart)?.length
  // The cart-expiry banner ("Your cart is no longer available") is an ecomm-only
  // experience — suppress it in OO. Other cart errors (e.g. out-of-stock) still show.
  const displayedError =
    channel !== Channel.ECOMM && error?.kind === 'CartError' ? null : error
  const hasInvalidContent = useCallback(
    (selection: CartSelection) =>
      hasInvalidContentProblemForSelection(selection, validationProblems),
    [validationProblems]
  )

  return (
    <div className='cartData'>
      <OrderItems
        error={displayedError}
        selections={cart?.order?.selections}
        hasItems={Boolean(hasItems && cartGuid)}
        ItemComponent={CartItemV2}
        itemProps={{
          cartGuid: cartGuid,
          editable,
          hasInvalidContent:
            validationProblems.length > 0 ? hasInvalidContent : undefined,
          itemSource
        }}
        noItemsComponent={
          <div className='emptyCart'>{t('cart.add-items-to-start-order')}</div>
        }
      />
    </div>
  )
}

export const CartPrices = ({
  giftCardAppliedAmount,
  tip,
  fundraisingAmount,
  shippingMessage
}: {
  giftCardAppliedAmount?: number
  tip?: number
  fundraisingAmount?: number
  shippingMessage?: string
}) => {
  const { cart, loadingCart, subtotal, channel } = useCart()
  const hasItems = !!getCheckoutEligibleItems(cart)?.length

  const cartLineItems: CartLineItems = useCartLineItems(cart as any)(
    tip,
    fundraisingAmount
  )

  const fundedOffersTotal = getAppliedFundedOffersTotal(
    cart?.appliedFundedOffers
  )
  // For ecommerce orders, use subtotal excluding shipping; otherwise use regular subtotal
  const displaySubtotal =
    channel === Channel.ECOMM
      ? calculateSubtotalExcludingShippingFromCart(cart)
      : subtotal

  return (
    <>
      {displaySubtotal && hasItems ? (
        <OrderPrices
          shippingMessage={shippingMessage}
          loading={loadingCart}
          subtotal={displaySubtotal}
          discountsTotal={calculateDiscountsTotal(
            cart?.order?.discounts?.globalReward?.amount || 0,
            fundedOffersTotal,
            cart?.order?.discountsTotal
          )}
          diningOptionBehavior={cart?.diningOptionBehavior}
          deliveryFee={cartLineItems.deliveryChargeTotalAmount || 0}
          gratuityServiceCharges={cartLineItems.gratuityServiceCharges || 0}
          processingServiceCharges={cartLineItems.processingServiceCharges || 0}
          nonDeliveryNonGratuityNonUbpServiceCharges={
            cartLineItems.nonDeliveryNonGratuityNonUbpServiceCharges || 0
          }
          tax={cartLineItems.tax || 0}
          tip={cartLineItems.tipAmount}
          fundraisingAmount={cartLineItems.fundraisingAmount}
          giftCardAppliedAmount={giftCardAppliedAmount}
        />
      ) : null}
    </>
  )
}

type CheckoutButtonProps = {
  isCheckoutPage: boolean
  onClose: () => void
  checkoutHref: string
  onCheckoutClick: (e: React.MouseEvent) => void
  total: number
}

const CheckoutButton = ({
  isCheckoutPage,
  onClose,
  checkoutHref,
  onCheckoutClick,
  total
}: CheckoutButtonProps) => {
  const buttonsConfig = useButtonsConfig()
  // On checkout page, use a button that just closes the popover
  if (isCheckoutPage) {
    return (
      <button type='button' className='checkoutButtonAction' onClick={onClose}>
        <div
          className='checkoutButton checkoutPage'
          data-testid='cart-checkout-cta'
        >
          <span>{t('cart.continue-checkout')}</span>
          <span>
            <FormattedPrice value={total} />
          </span>
        </div>
      </button>
    )
  }

  // Otherwise, use a Link to navigate to checkout
  return (
    <Link
      className='checkoutButtonAction'
      tabIndex={0}
      href={checkoutHref}
      onClick={onCheckoutClick}
      sameTab // int'l rxs with custom domains checkout on toasttab.com, so make sure they stay in the same tab
      blockAttribution // dont add attribution for int'l rxs that are forced to checkout on order.toasttab.com
    >
      <div
        className='checkoutButton'
        style={{ borderRadius: `${buttonsConfig.borderRadius}px` }}
        data-testid='cart-checkout-cta'
      >
        <span>{t('cart.checkout')}</span>
        <span>
          <FormattedPrice value={total} />
        </span>
      </div>
    </Link>
  )
}

const CartContent = () => {
  const [collapsed, setCollapsed] = useState(false)
  const { cart, channel, loadingCart, cartGuid } = useCart()
  const { isEditor } = useEditor()
  const {
    ooRestaurant,
    orderingDisabled,
    restaurant,
    ecommSettings,
    toastProduct
  } = useRestaurant()
  const { checkoutPath, ecommCheckoutPath, orderPath, ecommPath } =
    useRestaurantRoutes()
  const { pathname } = useLocation()
  const path = channel === Channel.ECOMM && ecommPath ? ecommPath : orderPath

  const isOOCheckoutPage = pathname === checkoutPath
  const isEcommCheckoutPage = pathname === ecommCheckoutPath
  const isCheckoutPage = isOOCheckoutPage || isEcommCheckoutPage
  const { fulfillmentData } = useFulfillment()
  const { setDeliveryModalOpen } = useDelivery()
  const { selectedUpsellItem, handleUpsellItemClicked } = useUpsell()
  const { announce } = useA11y()
  const {
    isOpen: itemModalOpen,
    onClose: closeItemModal,
    onOpen: openItemModal
  } = useModal()
  const isIntlRestaurant = useIsIntlRestaurant()
  const { ooIntlUseToasttabCheckout } = useFlags()
  // PageDomains are only provided for SitesRoutes, so this host won't be defined for Toast Local or OO on order.toasttab.com
  const { host } = usePageDomains()

  // Track pending upsell add to cart actions for screen reader announcement
  const [pendingUpsellAnnouncement, setPendingUpsellAnnouncement] = useState<
    string | null
  >(null)
  const prevLoadingRef = useRef(loadingCart)

  const handleUpsellClick = useCallback(
    async (
      item: UpsellItem,
      openItemModal: () => void,
      itemSource: ItemSource
    ) => {
      setPendingUpsellAnnouncement(item.name)
      await handleUpsellItemClicked(item, openItemModal, itemSource)
    },
    [handleUpsellItemClicked]
  )

  const upsellTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  // Cleanup announcement timeouts if component is unmounted
  useEffect(() => {
    return () => {
      if (upsellTimeoutRef.current) clearTimeout(upsellTimeoutRef.current)
    }
  }, [])

  // Announce when upsell was successfully added to the cart
  useEffect(() => {
    const wasLoading = prevLoadingRef.current
    prevLoadingRef.current = loadingCart

    if (wasLoading && !loadingCart && pendingUpsellAnnouncement) {
      upsellTimeoutRef.current = setTimeout(() => {
        announce(
          t('cart-item.added', {
            itemName: pendingUpsellAnnouncement || t('cart-item.item')
          })
        )
      }, 300)
      setPendingUpsellAnnouncement(null)
    }
  }, [loadingCart, pendingUpsellAnnouncement, announce])

  const { validationProblems } = useValidateCartForShipping()
  const shippingEnabled =
    channel === Channel.ECOMM &&
    ecommSettings?.fulfillmentConfig?.shippingSettings?.enabled

  const handleCloseItemModal = useCallback(() => {
    closeItemModal()
    window.history.replaceState(null, '', path)
  }, [closeItemModal, path])

  const cartPopoverContext = usePopoverContext(CART_POPOVER_CONTEXT_KEY)
  const isShippingEnabled =
    ecommSettings?.fulfillmentConfig?.shippingSettings?.enabled
  const { data, loading: shippingOptionsLoading } = useGetShippingOptionsQuery({
    variables: { input: { siteGuid: restaurant.id || '' } },
    skip: channel !== Channel.ECOMM || !restaurant.id || !isShippingEnabled,
    fetchPolicy: 'no-cache'
  })

  const freeShippingThreshold = data?.getServiceGroups.find(
    (group) => group?.type === 'FREE_SHIPPING'
  )?.freeShippingThresholdMin
  const shippingMessage =
    isShippingEnabled && freeShippingThreshold && cart?.order?.totalV2
      ? cart.order.totalV2 >= parseFloat(freeShippingThreshold)
        ? 'FREE'
        : 'Calculated at checkout'
      : undefined

  const handleCheckoutClick = useCallback(
    (e: React.MouseEvent) => {
      // Check if delivery address is needed for checkout
      const needsDeliveryAddress =
        fulfillmentData?.cartFulfillmentData?.diningOptionBehavior ===
          DiningOptionBehavior.Delivery &&
        !fulfillmentData.cartFulfillmentData.deliveryInfo?.address1

      if (needsDeliveryAddress) {
        e.preventDefault()
        // Close the cart modal immediately
        cartPopoverContext?.close()
        // Open the checkout requirement modal
        setDeliveryModalOpen(true, 'checkout-requirement')
      } else {
        // Close the cart modal when navigating to checkout
        // This is necessary because providers stay mounted across ecommerce routes
        cartPopoverContext?.close()
      }
    },
    [fulfillmentData, cartPopoverContext, setDeliveryModalOpen]
  )

  const checkoutHref = useMemo(() => {
    if (channel === Channel.ECOMM && ecommCheckoutPath) return ecommCheckoutPath
    if (
      ooIntlUseToasttabCheckout &&
      isIntlRestaurant &&
      host &&
      host !== resources.toastOrderHost &&
      (toastProduct === ToastProduct.OOPro ||
        toastProduct === ToastProduct.Sites) &&
      ooRestaurant?.shortUrl &&
      cartGuid
    ) {
      // Adyen limits the number of domains we can register for ApplePay, so we checkout on order.toasttab.com as a workaround
      return getToasttabCheckoutUrl(ooRestaurant.shortUrl, cartGuid)
    }
    return checkoutPath
  }, [
    checkoutPath,
    channel,
    ecommCheckoutPath,
    ooIntlUseToasttabCheckout,
    isIntlRestaurant,
    host,
    ooRestaurant,
    cartGuid,
    toastProduct
  ])

  if ((channel !== Channel.ECOMM && orderingDisabled) || !ooRestaurant) {
    return null
  }

  if (collapsed) {
    return (
      <div className='collapsedCart'>
        <button
          type='button'
          className='checkoutButton'
          onClick={() => setCollapsed(false)}
        >
          <span>
            {t('cart.view-cart', {
              count: cart?.order?.numberOfSelections || 0
            })}
          </span>
          <span>{<FormattedPrice value={cart?.order?.totalV2 || 0} />}</span>
        </button>
      </div>
    )
  }

  const hasItems = !!getCheckoutEligibleItems(cart)?.length

  return (
    <>
      <div id='cart-control' className='cart'>
        <div className='body'>
          <div className='paddedContent' data-testid='cart-items-container'>
            <CartItems
              editable
              itemSource={ItemSource.Cart}
              validationProblems={
                shippingEnabled ? validationProblems : undefined
              }
            />
            {!isEditor && isCheckoutPage && (
              <ButtonLink
                className='addItemButton'
                type='button'
                data-testid='cart-add-item-button'
                href={isEcommCheckoutPage ? ecommPath || '/store' : orderPath}
              >
                {t('cart.add-item')}
              </ButtonLink>
            )}
            {cart && channel !== Channel.ECOMM && (
              <div className='upsellContainer'>
                <Upsell
                  onItemSelect={(item: UpsellItem) =>
                    handleUpsellClick(item, openItemModal, ItemSource.Upsell)
                  }
                />
              </div>
            )}
          </div>
          <div>
            {cart &&
              channel === Channel.ECOMM &&
              isShippingEnabled &&
              freeShippingThreshold && (
                <FreeShippingProgressBar
                  threshold={parseFloat(freeShippingThreshold)}
                  loading={shippingOptionsLoading || loadingCart}
                />
              )}
          </div>
        </div>
        <FreeDeliveryProgress />
        <CartOffer />
        <div className='footer'>
          <CartPrices shippingMessage={shippingMessage} />
          {hasItems && !isEditor && (
            <CheckoutButton
              isCheckoutPage={isCheckoutPage}
              checkoutHref={checkoutHref}
              onClose={() => cartPopoverContext?.close()}
              onCheckoutClick={handleCheckoutClick}
              total={
                // Not ideal, but funded offers aren't priced with the order so we have to do the math here
                (cart?.order?.totalV2 || 0) -
                getAppliedFundedOffersTotal(cart?.appliedFundedOffers)
              }
            />
          )}
        </div>
      </div>
      {selectedUpsellItem && (
        <ItemModal
          isOpen={itemModalOpen}
          onClose={handleCloseItemModal}
          itemGuid={selectedUpsellItem.itemGuid}
          itemGroupGuid={selectedUpsellItem.itemGroupGuid}
          itemSource={ItemSource.Upsell}
        />
      )}
    </>
  )
}

/*
 * NOTE: Though the A11yProvider is wrapped at a higher level on OnlineOrderingProviders and CheckoutPage,
 * the screen reader cannot access the announce div because it is placed higher up in the DOM tree and outside of the cart PORTAL
 * This ensures that the cart receives its own announce element that the screen reader can identify when actions occur in the cart.
 */
const Cart = () => {
  return (
    <A11yProvider>
      <CartContent />
    </A11yProvider>
  )
}

export default Cart
